import { Skull, Flame, WandSparkles, PartyPopper, Rabbit, Sun } from 'lucide-react'
import type { LucideIcon } from 'lucide-react'

export const INVASION_BG = 'https://cdn.magicpatterns.com/patterns/generated-images/ef88a183-8fb6-4cc0-8d15-6f388800eec9.jpg'

export type RewardGroup = { who: string; items: string[] }

/**
 * Arte oficial de cada invasión. Los pósters del staff ya llevan el nombre de la invasión
 * en la imagen (hasTitle). Red Dragon usa una ilustración sin texto, así que el nombre se superpone.
 */
export type InvasionPoster = { src: string; hasTitle: boolean }

export type BossInvasion = {
  id: string
  name: string
  icon: LucideIcon
  highlight: string
  location: string
  spawn: string
  perDay: number
  rewards: RewardGroup[]
  poster: InvasionPoster
}

export const bossInvasions: BossInvasion[] = [
  {
    id: 'death-king',
    name: 'Death King / Skeleton',
    icon: Skull,
    highlight: 'Ítems tier 1–4, de +6 a +9',
    location: 'Lorencia o Noria',
    spawn: '1 jefe + 10 acompañantes',
    perDay: 4,
    rewards: [
      { who: 'Jefe', items: ['2 joyas garantizadas del reparto general.'] },
      { who: 'Cada acompañante', items: ['1 ítem garantizado: pieza de set o arma tier 1–4, de +6 a +9, NO Excellent.'] },
    ],
    poster: { src: 'https://cdn.magicpatterns.com/uploads/jaPdWtrXy3mRRyo5HWbmyr/51ABDB52-11D7-459E-8280-EFF4E47E983C.png', hasTitle: true },
  },
  {
    id: 'red-dragon',
    name: 'Red Dragon',
    icon: Flame,
    highlight: 'Ítems tier 4–7, +9 o +10',
    location: '1 dragón en Lorencia, 1 en Devias y 1 en Noria',
    spawn: '3 dragones en total',
    perDay: 2,
    rewards: [
      {
        who: 'Cada dragón',
        items: [
          '2 joyas garantizadas del reparto general.',
          '2 ítems garantizados: piezas de set o armas tier 4–7, nivel +9 o +10, NO Excellent.',
        ],
      },
    ],
    poster: { src: 'https://cdn.magicpatterns.com/patterns/generated-images/bc5ca11a-1f27-4f5a-a126-8487db8843c8.jpg', hasTitle: false },
  },
  {
    id: 'white-wizard',
    name: 'White Wizard y sus soldados',
    icon: WandSparkles,
    highlight: 'Posible ítem Excellent',
    location: 'Un mapa elegido entre Lorencia, Devias y Noria',
    spawn: '1 jefe + 10 soldados + 10 arqueros',
    perDay: 2,
    rewards: [
      {
        who: 'Jefe',
        items: ['3 joyas garantizadas del reparto general.', '20% de probabilidad de obtener un ítem Excellent adicional.'],
      },
      { who: 'Cada acompañante', items: ['20%: Wizard’s Ring.', '80%: 75.000 Zen.'] },
    ],
    poster: { src: 'https://cdn.magicpatterns.com/uploads/7hm2WMucrEKEv4ra8RzSBK/4DAED6FE-0874-43EC-AD66-85D6D2A2EA63.png', hasTitle: true },
  },
]

export type JewelInvasion = {
  id: string
  name: string
  icon: LucideIcon
  maps: string
  monsters: number
  perDay: number
  drops: { jewel: string; pct: number }[]
  poster: InvasionPoster
}

export const jewelInvasions: JewelInvasion[] = [
  {
    id: 'new-year',
    name: 'New Year',
    icon: PartyPopper,
    maps: 'Atlans, Lost Tower o Aida',
    monsters: 10,
    perDay: 4,
    drops: [{ jewel: 'Jewel of Chaos', pct: 70 }, { jewel: 'Jewel of Soul', pct: 30 }],
    poster: { src: 'https://cdn.magicpatterns.com/uploads/qWBJpGB29rnpWcws6EDT35/C610B80D-67D5-4FE0-B05C-B974E29D1B6A.png', hasTitle: true },
  },
  {
    id: 'rabbits',
    name: 'Rabbits',
    icon: Rabbit,
    maps: 'Tarkan, Lost Tower o Lorencia',
    monsters: 10,
    perDay: 4,
    drops: [{ jewel: 'Jewel of Chaos', pct: 70 }, { jewel: 'Jewel of Bless', pct: 30 }],
    poster: { src: 'https://cdn.magicpatterns.com/uploads/2UsKYJuv67mFE6nmYQ9G6m/8DB83B16-D5AA-4E96-A636-8A3280A97FF1.png', hasTitle: true },
  },
  {
    id: 'summer',
    name: 'Summer',
    icon: Sun,
    maps: 'Devias, Noria o Lorencia',
    monsters: 10,
    perDay: 4,
    drops: [{ jewel: 'Jewel of Life', pct: 50 }, { jewel: 'Jewel of Creation', pct: 50 }],
    poster: { src: 'https://cdn.magicpatterns.com/uploads/tNS1TFUCBBnUuhraWeuwcd/AEED6243-349A-4D55-A48E-59F903A576E8.png', hasTitle: true },
  },
]

export const rewardTotals = [
  { name: 'Death King', jewels: 8, items: 40, extra: '' },
  { name: 'Red Dragon', jewels: 12, items: 12, extra: '' },
  { name: 'White Wizard', jewels: 6, items: 0, extra: '+ posibles Excellent y premios de acompañantes' },
  { name: 'New Year', jewels: 40, items: 0, extra: '' },
  { name: 'Rabbits', jewels: 40, items: 0, extra: '' },
  { name: 'Summer', jewels: 40, items: 0, extra: '' },
]

export const progressionTargets = ['Rabbit', 'Dragón Dorado', 'Medusa']

export const activityEvents = ['Invasiones', 'Chaos Castle', 'Blood Castle', 'Devil Square', 'Eventos de VANGUARD']

export const strategyQuestions = ['¿Se dividirán para buscar?', '¿Irán juntos?', '¿Defenderán una zona?']
